import { formatCurrency } from '@/services/string';
import { FieldConfig, OutputConfig, SectionConfig } from '../types/common';
import z from 'zod';
import { calculateMonthlyPayment } from '@/services/calculations/monthlyPayment';

const MonthlyPaymentInputBaseSchema = z.object({
  paymentType: z.enum(['Interest Only', 'Fully Amortizing'], {
    message: 'Amortization type is required'
  }),
  loanAmount: z
    .number({
      message: 'Loan amount is required'
    })
    .min(1, { message: 'Loan amount must be at least $1' }),
  termLength: z.coerce.number().optional(),
  interestRate: z.coerce
    .number({
      message: 'Interest rate is required'
    })
    .min(0.01, { message: 'Interest rate must be at least 0.01%' })
    .max(100, { message: 'Interest rate cannot exceed 100%' })
});

export type MonthlyPaymentInput = z.infer<typeof MonthlyPaymentInputBaseSchema>;

export const MonthlyPaymentKeys = MonthlyPaymentInputBaseSchema.keyof().options;

export const MonthlyPaymentInputSchema = MonthlyPaymentInputBaseSchema.refine(
  (data) => {
    if (data.paymentType === 'Fully Amortizing') {
      return data.termLength !== undefined && data.termLength > 0;
    }
    return true;
  },
  {
    message: 'Term length is required for amortized loans',
    path: ['termLength']
  }
);

export const MonthlyPaymentSectionConfig: SectionConfig<MonthlyPaymentInput> = [
  {
    fields: ['paymentType', 'loanAmount', 'termLength', 'interestRate']
  }
];

export const getMonthlyPaymentFieldConfig = (
  data: MonthlyPaymentInput
): FieldConfig<MonthlyPaymentInput> => ({
  paymentType: {
    name: 'paymentType',
    type: 'toggle-group',
    label: 'Payment Type',
    options: [
      { label: 'Interest Only', value: 'Interest Only' },
      { label: 'Amortized', value: 'Fully Amortizing' }
    ]
  },
  loanAmount: {
    name: 'loanAmount',
    type: 'currency',
    label: 'Loan amount',
    placeholder: '100,000.00'
  },
  termLength: {
    name: 'termLength',
    type: 'number',
    label: 'Amortization Term',
    placeholder: '56',
    suffix: 'months',
    hide: data.paymentType === 'Interest Only' || !data.paymentType
  },
  interestRate: {
    name: 'interestRate',
    type: 'percentage',
    label: 'Interest Rate',
    placeholder: '6.00'
  }
});

export const getMonthlyPaymentOutputConfig = (
  data: MonthlyPaymentInput
): OutputConfig => [
  {
    fields: [
      {
        label: 'Monthly Payment',
        value: formatCurrency(
          calculateMonthlyPayment({
            totalLoanAmount: data.loanAmount,
            interestRate: data.interestRate,
            amortizationTerm:
              data.paymentType === 'Fully Amortizing' ? data.termLength : 0,
            amortizationType: data.paymentType
          })
        )
      }
    ]
  }
];
